import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { ThirdPartyReferenceType } from '@lsq-frontend/remote-services/all-java/common-mk0';
import {
    GetAccountSummaryForUserService,
    NetworkRequestInformation
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { CompleteEnrollmentService } from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import { VendorPortalEnrollmentDetails } from '@lsq-frontend/remote-services/enterprise/common';
import { Color, ignoreNullish, Modal } from '@lsq-frontend/common-ui';
import { FeatureFlagService, Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { ModalWorkflowService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { keyBy } from 'lodash';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import { EnrollmentService } from '../../enrollment/enrollment.service';
import { AddBankAccountFormService } from '../../supply-chain/bank-accounts/add-bank-account-form.service';
import { AddIntlBankAccountFormService } from '../../supply-chain/bank-accounts/add-intl-bank-account-form.service';
import { AddBankAccountControllerComponent } from '../../supply-chain/bank-accounts/bank-account-form-controller.component';
import { BankAccountFormControllerService } from '../../supply-chain/bank-accounts/bank-account-form-controller.service';

interface CompanyToSelect {
    name: string;
    relationshipName: string;
    thirdPartyReferenceType: ThirdPartyReferenceType;
    companyKey: string;
    sellerKey: string;
    hasChildren: boolean;
}

@Component({
    selector: 'switch-account',
    templateUrl: './switch-account.component.html',
    styleUrls: ['./switch-account.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SwitchAccountComponent implements OnInit {
    @ViewChild('enrollmentSupplierAgreementModalBody')
    public enrollmentSupplierAgreementModalBody!: TemplateRef<any>;
    @ViewChild('vendorAgreementModalBody')
    public vendorAgreementModalBody!: TemplateRef<any>;
    @ViewChild('newAccountAddedConfirmationModalBody')
    public newAccountAddedConfirmationModalBody!: TemplateRef<any>;
    public acceptedTermsForm = new FormGroup({
        acceptedTerms: new FormControl({ value: false, disabled: true })
    });
    @ViewChild('enrollmentPaymentMethodModalBody')
    public enrollmentPaymentMethodModalBody: TemplateRef<any>;
    public readonly invoiceFactoring = this._i18n({ value: 'AR Financing' });
    public readonly amazonCognito = this._i18n({ value: 'Amazon Cognito' });
    public readonly apply = this._i18n({ value: 'Apply' });
    public readonly enrollment = this._i18n({ value: 'Enrollment' });
    public readonly supplyChainFinance = this._i18n({ value: 'AP Financing' });
    public readonly thirdPartyReferenceType = ThirdPartyReferenceType;

    public readonly vendorAgreementAcceptText = this._i18n({ value: 'Accept Terms and Continue' });
    public readonly vendorAgreementCancelText = this._i18n({ value: 'Cancel' });
    private readonly _enrollmentPaymentMethodOkButtonText = this._i18n({
        value: 'Submit Payment Method and Add Buyer'
    });
    private readonly _enrollmentModalTitlePrefix = this._i18n({ value: 'Enroll with' });

    public readonly enrollModalCompleteTitle = this._i18n('has been added!');
    public readonly backToSwitchAccountsButtonText = this._i18n('Back to Select Accounts');
    public readonly startManagingAccountButtonText = this._i18n('Start Managing This Account');

    public readonly icons = Icons;
    public readonly color = Color;
    public readonly applicationNames = {
        [ThirdPartyReferenceType.FACTORSOFT]: this.invoiceFactoring,
        [ThirdPartyReferenceType.AMAZON_COGNITO]: this.amazonCognito, // Should not show up
        [ThirdPartyReferenceType.APPLY]: this.apply, // Should not show up
        [ThirdPartyReferenceType.ENROLLMENT]: this.enrollment, // Should not show up
        [ThirdPartyReferenceType.FINANCING_SELLER]: this.supplyChainFinance,
        [ThirdPartyReferenceType.FINANCING_BUYER]: this.supplyChainFinance,
        [ThirdPartyReferenceType.FINANCING_ACCOUNT]: this.supplyChainFinance
    };
    public readonly applicationColors = {
        [ThirdPartyReferenceType.FACTORSOFT]: 'green',
        [ThirdPartyReferenceType.AMAZON_COGNITO]: 'black', // Should not show up
        [ThirdPartyReferenceType.APPLY]: 'black', // Should not show up
        [ThirdPartyReferenceType.ENROLLMENT]: 'black', // Should not show up
        [ThirdPartyReferenceType.FINANCING_SELLER]: 'indigo',
        [ThirdPartyReferenceType.FINANCING_BUYER]: 'indigo',
        [ThirdPartyReferenceType.FINANCING_ACCOUNT]: 'indigo'
    };

    private _companiesToSelect$ = new BehaviorSubject<CompanyToSelect[] | undefined>(undefined);
    public companiesDataSource = new LsqListSource({
        rows$: this._companiesToSelect$.pipe(ignoreNullish(), shareReplay(1)),
        loading$: this._companiesToSelect$.pipe(
            map((maybeCompanies) => maybeCompanies === undefined)
        )
    });
    public childCompaniesByParentCompanyKey!: Record<string, CompanyToSelect[]>;
    public expandedParentCompanyKeys = new Set<string>();
    public networkRequests!: NetworkRequestInformation[];
    public aCompanyHasBeenSelected = false;
    public enrollmentSubmittingVendorAgreement$ = new BehaviorSubject(false);
    public enrollmentSubmittingPaymentMethod$ = new BehaviorSubject(false);
    public enrollmentBuyerName: string;
    public queryParams?: Record<string, string>;

    constructor(
        private _userService: UserService,
        private _i18n: I18n,
        public responsiveService: ResponsiveService,
        private _enrollmentService: EnrollmentService,
        private _getAccountSummaryForUserService: GetAccountSummaryForUserService,
        private _modalWorkflowService: ModalWorkflowService,
        private _addBankAccountFormService: AddBankAccountFormService,
        private _completeEnrollmentService: CompleteEnrollmentService,
        private _bankAccountFormControllerService: BankAccountFormControllerService,
        private _addIntlBankAccountFormService: AddIntlBankAccountFormService,
        private _featureFlagService: FeatureFlagService,
        private _activatedRoute: ActivatedRoute
    ) {}

    public async ngOnInit(): Promise<void> {
        await this._initialize();

        this.queryParams = await this._activatedRoute.queryParams.pipe(first()).toPromise();
        const sellerEnrollmentToken = this.queryParams[
            FastTrackRouteQueryParams.SellerEnrollmentToken
        ];
        const networkRequestToAutoOpen = this.networkRequests.find(
            (networkRequest) => networkRequest.enrollmentToken === sellerEnrollmentToken
        );
        if (networkRequestToAutoOpen) {
            await this.startEnrollmentWorkflow(networkRequestToAutoOpen);
        }
    }

    private async _initialize(): Promise<void> {
        const user = await this._userService.user$.pipe(first()).toPromise();
        const accountSummary = await this._getAccountSummaryForUserService.executeOnce({
            refUserKey: user.userKey
        });
        const userCompaniesByCompanyKey = keyBy(user.companies, 'companyKey');

        this.networkRequests = accountSummary.networkRequestInformation;
        this._companiesToSelect$.next(
            accountSummary.parentCompanies.map((company) => {
                const userCompany = userCompaniesByCompanyKey[company.companyKey];
                return {
                    name: company.name,
                    relationshipName:
                        accountSummary.buyerNamesBySellerKey[userCompany.alternateCompanyKey],
                    thirdPartyReferenceType: company.thirdPartyReferenceType,
                    companyKey: company.companyKey,
                    sellerKey: userCompany.alternateCompanyKey,
                    hasChildren: company.hasChildren
                };
            })
        );
        this.childCompaniesByParentCompanyKey = accountSummary.childCompanies.reduce(
            (childCompaniesByParentCompanyKey, childCompany) => {
                const currentListForParentCompanyKey =
                    childCompaniesByParentCompanyKey[childCompany.parentCompanyKey] ?? [];
                return {
                    ...childCompaniesByParentCompanyKey,
                    [childCompany.parentCompanyKey]: [
                        ...currentListForParentCompanyKey,
                        childCompany
                    ]
                };
            },
            {} as Record<string, CompanyToSelect[]>
        );

        // Expand all groups.
        (await this.companiesDataSource.rows$.pipe(first()).toPromise()).forEach((row, index) => {
            if (row.hasChildren) {
                void this.handleParentCompanyToggleClick(row, index);
            }
        });
    }

    public async handleCompanyClick(companyKey: string): Promise<void> {
        if (!this.aCompanyHasBeenSelected) {
            this.aCompanyHasBeenSelected = true;
            await this._userService.selectCompany(companyKey);
            if (this.queryParams.showUpdateMfaModal === 'true') {
                await this._userService.navigateToHome({
                    showUpdateMfaModal: this.queryParams.showUpdateMfaModal
                });
            } else {
                await this._userService.navigateToHome();
            }
        }
    }

    public async startEnrollmentWorkflow(networkRequest: NetworkRequestInformation): Promise<void> {
        const { enrollmentToken } = networkRequest;
        this._addBankAccountFormService.initFormGroup();
        const enrollmentDetails = await this._enrollmentService.getEnrollmentDetails(
            networkRequest.enrollmentToken
        );
        let workflow: Modal[] = [
            await this._getPaymentMethodModal(enrollmentToken, enrollmentDetails),
            this._getNewAccountAddedConfirmationModal(
                enrollmentDetails.buyerName,
                enrollmentDetails.refCompanyKey
            )
        ];
        if (!enrollmentDetails.skipVendorAgreement) {
            workflow = [this._getVendorAgreementModal(enrollmentDetails.buyerName), ...workflow];
        }
        this._modalWorkflowService.showWorkflow(workflow);
    }

    private _getVendorAgreementModal(buyerName: string): Modal {
        return new Modal({
            templateRef: this.enrollmentSupplierAgreementModalBody,
            context: this,
            showHeader: true,
            showFooter: true,
            okButtonSize: 'block',
            okButtonText: this.vendorAgreementAcceptText,
            showOkButton: true,
            okButtonIsDisabled$: this.acceptedTermsForm.valueChanges.pipe(
                map((formValue) => !formValue.acceptedTerms),
                startWith(true)
            ),
            isSubmitting$: this.enrollmentSubmittingVendorAgreement$,
            showCloseButton: true,
            showCancelButton: true,
            cancelButtonText: this.vendorAgreementCancelText,
            title: this._enrollmentModalTitlePrefix + ' ' + buyerName,
            width: '82.2rem',
            onOkClick: async () => {
                this._modalWorkflowService.nextStep();
            },
            onBackdropClick: () => this._cancelEnrollment(),
            onCancelClick: () => this._cancelEnrollment(),
            onCloseButtonClick: () => this._cancelEnrollment()
        });
    }

    private async _getPaymentMethodModal(
        enrollmentToken: string,
        enrollmentDetails: VendorPortalEnrollmentDetails
    ): Promise<Modal> {
        if (this._featureFlagService.isOnSync(FeatureFlags.InternationalWiresPhase1)) {
            await this._bankAccountFormControllerService.initFormGroup({
                companyAddress: enrollmentDetails.primaryCompanyAddress,
                companyName: enrollmentDetails.companyName
            });
            return new Modal({
                title: `${this._enrollmentModalTitlePrefix} ${enrollmentDetails.buyerName}`,
                bodyComponent: AddBankAccountControllerComponent,
                showCancelButton: true,
                showCloseButton: true,
                showFooter: true,
                showHeader: true,
                showOkButton: true,
                okButtonSize: 'block',
                okButtonText: this._enrollmentPaymentMethodOkButtonText,
                width: '82.2rem',
                isSubmitting$: this.enrollmentSubmittingPaymentMethod$,
                okButtonIsDisabled$: combineLatest([
                    this._addBankAccountFormService.disableSubmit$,
                    this._addIntlBankAccountFormService.disableSubmit$
                ]).pipe(
                    map(
                        ([bankFormDisabled, intlBankFormDisabled]) =>
                            bankFormDisabled && intlBankFormDisabled
                    ),
                    startWith(true)
                ),
                onOkClick: async () => {
                    try {
                        this.enrollmentSubmittingPaymentMethod$.next(true);
                        await this.completeEnrollment(enrollmentToken, enrollmentDetails);
                        this._modalWorkflowService.nextStep();
                    } finally {
                        this.enrollmentSubmittingPaymentMethod$.next(false);
                    }
                },
                onBackdropClick: () => {
                    if (!this.enrollmentSubmittingPaymentMethod$) {
                        this._cancelEnrollment();
                    }
                },
                onCancelClick: () => this._cancelEnrollment(),
                onCloseButtonClick: () => this._cancelEnrollment()
            });
        } else {
            return new Modal({
                title: `${this._enrollmentModalTitlePrefix} ${enrollmentDetails.buyerName}`,
                templateRef: this.enrollmentPaymentMethodModalBody,
                showCancelButton: true,
                showCloseButton: true,
                showFooter: true,
                showHeader: true,
                showOkButton: true,
                okButtonSize: 'block',
                okButtonText: this._enrollmentPaymentMethodOkButtonText,
                width: '82.2rem',
                isSubmitting$: this.enrollmentSubmittingPaymentMethod$,
                okButtonIsDisabled$: this._addBankAccountFormService.disableSubmit$,
                onOkClick: async () => {
                    try {
                        this.enrollmentSubmittingPaymentMethod$.next(true);
                        await this.completeEnrollment(enrollmentToken, enrollmentDetails);
                        this._modalWorkflowService.nextStep();
                    } finally {
                        this.enrollmentSubmittingPaymentMethod$.next(false);
                    }
                },
                onBackdropClick: () => {
                    if (!this.enrollmentSubmittingPaymentMethod$) {
                        this._cancelEnrollment();
                    }
                },
                onCancelClick: () => this._cancelEnrollment(),
                onCloseButtonClick: () => this._cancelEnrollment()
            });
        }
    }

    private _getNewAccountAddedConfirmationModal(buyerName: string, companyKey: string): Modal {
        this.enrollmentBuyerName = buyerName;

        return new Modal({
            title: `${buyerName} ${this.enrollModalCompleteTitle}`,
            templateRef: this.newAccountAddedConfirmationModalBody,
            context: this,
            cancelButtonText: this.backToSwitchAccountsButtonText,
            okButtonText: this.startManagingAccountButtonText,
            showOkButton: true,
            showCancelButton: true,
            showCloseButton: true,
            showFooter: true,
            width: '82rem',
            onCancelClick: () => this._modalWorkflowService.hideWorkflow(),
            onCloseButtonClick: () => this._modalWorkflowService.hideWorkflow(),
            onOkClick: async () => await this.handleCompanyClick(companyKey)
        });
    }

    public async completeEnrollment(
        enrollmentToken: string,
        enrollmentDetails: VendorPortalEnrollmentDetails
    ): Promise<void> {
        await this._enrollmentService.createAccount({
            firstName: enrollmentDetails.firstName,
            lastName: enrollmentDetails.lastName,
            emailAddress: enrollmentDetails.emailAddress,
            phoneNumber: enrollmentDetails.phoneNumber,
            token: enrollmentToken,
            password: undefined,
            companyKey: enrollmentDetails.companyKey,
            isNotFirstEnrollment: true
        });
        await this._enrollmentService.acceptSubsequentSupplierAgreement(
            enrollmentDetails.companyKey
        );
        if (this._featureFlagService.isOnSync(FeatureFlags.InternationalWiresPhase1)) {
            await this._bankAccountFormControllerService.submitForm(true, {
                sellerKey: enrollmentDetails.sellerKey,
                companyKey: enrollmentDetails.companyKey
            });
        } else {
            await this._addBankAccountFormService.submitForm(true, {
                sellerKey: enrollmentDetails.sellerKey,
                companyKey: enrollmentDetails.companyKey
            });
        }
        await this._completeEnrollmentService.executeOnce({
            relationshipKey: enrollmentDetails.relationshipKey
        });
        await this._initialize();
    }

    public async acceptVendorAgreement(sellerKey: string): Promise<void> {
        this.enrollmentSubmittingVendorAgreement$.next(true);
        try {
            await this._enrollmentService.acceptSubsequentSupplierAgreement(sellerKey);
        } finally {
            this.enrollmentSubmittingVendorAgreement$.next(false);
        }
    }

    public scrollAgreement(event: Event): void {
        const target = event.target as HTMLElement;
        const SCROLL_BUFFER = 10;
        if (target.scrollTop >= target.scrollHeight - target.offsetHeight - SCROLL_BUFFER) {
            this.acceptedTermsForm.get('acceptedTerms').enable();
        }
    }

    private _cancelEnrollment(): void {
        this.acceptedTermsForm.reset();
        this.acceptedTermsForm.get('acceptedTerms').disable();
        this._addBankAccountFormService.initFormGroup();
        this._modalWorkflowService.hideWorkflow();
    }

    public async handleParentCompanyToggleClick(
        row: CompanyToSelect,
        index: number
    ): Promise<void> {
        if (this.expandedParentCompanyKeys.has(row.companyKey)) {
            await this._collapseParentCompany(row.companyKey);
        } else {
            this._expandParentCompany(row.companyKey, index);
        }
    }

    private _expandParentCompany(parentCompanyKey: string, index: number): void {
        const childCompanies = this.childCompaniesByParentCompanyKey[parentCompanyKey];
        this.expandedParentCompanyKeys.add(parentCompanyKey);
        this.companiesDataSource.expandGroup(index, childCompanies);
    }

    private async _collapseParentCompany(parentCompanyKey: string): Promise<void> {
        this.expandedParentCompanyKeys.delete(parentCompanyKey);
        await this.companiesDataSource.collapseGroupAlt('parentCompanyKey', parentCompanyKey);
    }
}
